import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import Footer from "../components/Footer";
import DFNavbar from "../components/Navbar";
import { PRODUCT_SELECT_ROUTE, PRODUCT_COMPLETE_TRIAL_ROUTE } from "../consts/routes";
import { useUserContext } from "../contexts/UserContext";
import { getProducts } from "../clients/ProductClient";
import {
    PRODUCT_TYPE_ID_DF_SCANNER_LITE,
    PRODUCT_TYPE_ID_DF_SCANNER_PRO,
    productTypeOptions,
} from "../consts/constants";
import { checkoutFreeTrial } from "../clients/PaymentClient";
import { CrossCircleIcon } from "../components/Icons";
import { ScrollToTop } from "../components/ScrollToTop";
import { Product } from "../types/Product";
import { E400 } from "../consts/ErrorCodes";
import { Helmet } from "react-helmet";
import { PaymentHistoryWithLicenses } from "../types/PaymentHistoryWithLicenses";

/**
 * ライセンス購入確認画面（無料体験版用）
 * @returns JSX.Element
 */
const ProductConfirmTrial = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user, selectedProductType, getRecentToken } = useUserContext();
    const [errorMessage, setErrorMessage] = useState<string>("");

    /**
     * 処理中フラグ
     *
     * ※多重クリックを防止するため、外部通信を伴うイベント処理では必ず使用すること
     */
    const [isProcessing, setIsProcessing] = useState<boolean>(false);

    // ページ表示時にuseEffectが2回実行されるのを防ぐために使用。
    // ※React.StrictModeの仕様
    const effectRun = useRef(false);

    // ページ初期化処理
    useEffect(() => {
        if (!selectedProductType) {
            // コンテキストに商品タイプ情報がなければ選択画面へ戻る
            navigate(PRODUCT_SELECT_ROUTE);
        }
        if (!user) {
            // ページリロード時はコンテキストも初期化されるため、空の場合は一度リターンする
            // コンテキストが生成されユーザー情報が生成された時点で再実行する
            return;
        }
        if (!effectRun.current) {
            return () => {
                effectRun.current = true;
            };
        }
    }, [selectedProductType, user, navigate]);

    /**
     * 商品選択画面へ戻る押下時のイベント処理
     */
    const handleBackToSelection = () => {
        navigate(PRODUCT_SELECT_ROUTE);
    };

    /**
     * 「無料体験を利用する」押下時のイベント処理
     */
    const handleForwardToOrder = async () => {
        setErrorMessage("");
        if (isProcessing) return;
        setIsProcessing(true);
        try {
            // トークンチェック&取得
            const idToken = await getRecentToken();
            if (!idToken) {
                setErrorMessage(t("ErrorMessage.failToGetIdpToken"));
                return;
            }

            // [API]商品情報一覧取得
            const agencyId = 0;
            const productTypeId = selectedProductType?.id;
            if (productTypeId == undefined) return;
            const productList: Product[] = await getProducts(idToken, productTypeId, agencyId);

            // 無料体験ライセンスを抽出
            const licenseTypeId = 100;
            const targetProduct: Product | undefined = productList.find((p) => p.license_type_id == licenseTypeId);
            if (targetProduct == undefined) {
                setErrorMessage(t("ErrorMessage.freeTrialNotAvailable"));
                return;
            }

            // [API]Checkout API（無料体験）
            const productId: number = targetProduct.product_id;
            const selectedDate = new Date();
            try {
                const issuedResult: PaymentHistoryWithLicenses = await checkoutFreeTrial(
                    idToken,
                    productId,
                    user.attributes.email,
                    1, // 現状は1固定
                    selectedDate?.toISOString(),
                );
                navigate(PRODUCT_COMPLETE_TRIAL_ROUTE, { state: { issuedResult: issuedResult } });
            } catch (error: any) {
                if (error.response && error.response.data && error.response.data.code) {
                    const errorCode = error.response.data.code;
                    if (errorCode === E400) {
                        setErrorMessage(t("ErrorMessage.freeTrialAlreadyUsed"));
                    } else {
                        setErrorMessage(t("ErrorMessage.freeTrialNotAvailable"));
                    }
                } else {
                    setErrorMessage(t("ErrorMessage.freeTrialNotAvailable"));
                }
                return;
            }
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="flex min-h-screen flex-col">
            <Helmet title={t("ProductConfirmForTrial.meta.title")} />
            <ScrollToTop />
            <DFNavbar bottomPadding={true} />
            <div className="mx-auto max-w-3xl flex-grow items-center justify-center px-6 py-8 pt-20">
                <h1 className="h1-common py-3">{t("ProductConfirmForTrial.productConfirmTitle")}</h1>

                {/* エラーメッセージ */}
                {errorMessage && (
                    <div className="flex items-center justify-center p-2">
                        <CrossCircleIcon />
                        <span className="pl-1 text-center font-bold text-red-600">
                            {errorMessage.split("\n").map((line, index) => (
                                <div key={index}>{line}</div>
                            ))}
                        </span>
                    </div>
                )}

                {/* 購入内容表示パネル */}
                <div className="m-0 rounded-lg border border-gray-200 bg-yellow-50 p-2 pb-4 shadow sm:m-2">
                    <p className="mx-auto py-2 text-center text-lg font-bold">
                        {t("ProductConfirmForTrial.purchaseFollowing")}
                    </p>

                    {/* 商品名 */}
                    <div className="px-10 py-2 font-bold sm:flex sm:items-center">
                        <span className="">{t("CommonLabel.productName")}:&nbsp;&nbsp;&nbsp;</span>
                        <div id="product-name" className="">
                            {selectedProductType
                                ? productTypeOptions.find((p) => p.productTypeId == selectedProductType.id)
                                      ?.productTypeName +
                                  " " +
                                  t("ProductConfirmForTrial.freeTrialLicense")
                                : t("CommonLabel.unselected")}
                        </div>
                    </div>

                    {/* 有効期間 */}
                    <div className="px-10 py-2 font-bold sm:flex sm:items-center">
                        <span className="">{t("CommonLabel.validityPeriod")}:&nbsp;&nbsp;&nbsp;</span>
                        <div className="">{t("ProductConfirmForTrial.validityPeriodText")}</div>
                    </div>

                    <hr className="mx-8 my-2 border border-gray-300" />

                    {/* 合計金額 */}
                    <div className="flex items-center pl-10 pt-2 text-lg font-extrabold text-orange-600">
                        <span className="">{t("CommonLabel.totalAmount")}:&nbsp;</span>
                        <span>{t("CommonLabel.free")}</span>
                    </div>
                </div>

                {/* 購入前注意事項 */}
                <p className="list-disc px-10 pt-7 text-center text-xl font-bold text-red-600">
                    {t("ProductConfirmForTrial.checkBeforePurchasingTitle")}
                </p>
                <ul className="list-disc px-10 pb-8 pt-2 text-gray-800">
                    <li>
                        <Trans
                            i18nKey={"ProductConfirmForTrial.checkBeforePurchasingText1"}
                            components={{
                                b: <span className="font-bold" />,
                            }}
                        />
                    </li>
                    <li>
                        <Trans
                            i18nKey={"ProductConfirmForTrial.checkBeforePurchasingText2"}
                            components={{
                                b: <span className="font-bold" />,
                            }}
                        />
                    </li>
                    {selectedProductType?.id &&
                        [PRODUCT_TYPE_ID_DF_SCANNER_PRO, PRODUCT_TYPE_ID_DF_SCANNER_LITE].includes(
                            selectedProductType?.id,
                        ) && (
                            <li>
                                <Trans
                                    i18nKey={"ProductConfirmForTrial.checkBeforePurchasingText3"}
                                    components={{
                                        b: <span className="font-bold" />,
                                    }}
                                />
                            </li>
                        )}
                </ul>

                {/* 戻る/進むボタン */}
                <div className="flex justify-evenly pb-5 pt-2 text-sm font-medium">
                    <button onClick={handleBackToSelection} className="btn-secondary w-1/3">
                        {t("ProductConfirmForTrial.backToProductSelect")}
                    </button>
                    <button
                        id="payment-btn"
                        onClick={handleForwardToOrder}
                        className={`${isProcessing ? "btn-primary-disabled" : "btn-primary"} w-1/3`}
                        disabled={isProcessing}
                    >
                        {isProcessing ? (
                            <div className="flex items-center justify-center">
                                <div className="mr-1 h-4 w-4 animate-spin rounded-full border-2 border-lime-100 border-t-transparent"></div>
                                <label className="">Processing...</label>
                            </div>
                        ) : (
                            t("ProductConfirmForTrial.orderFreeTrial")
                        )}
                    </button>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ProductConfirmTrial;
